import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableFilters} from '../../Components/Toolkit/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import WithRouter from '../../Toolkit/WithRouter';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MessageDialog from '../../Dialogs/MessageDialog';
import {DrawingClient} from '../../Api/DrawingClient';
import {DrawingRegister, DrawingSummary, DrawingSummaryReply} from '../../Api/ControllerTypes/DrawingTypes';
import MonthTotalsControl from '../../Components/MonthTotalsControl';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {InputNumber, InputNumberChangeParams} from 'primereact/inputnumber';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {MNullable} from '../../Toolkit/Utils';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {Divider} from 'primereact/divider';
import TurnaroundControl from '../../Components/TurnaroundControl';
import {Calendar} from 'primereact/calendar';
import MMultiDropDown from '../../Toolkit/Components/Controls/MultiDropDown';
import {FilterMatchMode} from 'primereact/api';
import EnumContext from '../../Context/enum-context';
import {LockReply} from '../../Api/ControllerTypes/LockTypes';
import {MSuccessIcon} from '../../Toolkit/Icons';
import {CreateLockRequest, LockClient} from '../../Api/LockClient';
import {DataTable, DataTableExportFunctionParams} from 'primereact/datatable';
import MButton from '../../Toolkit/Components/Controls/Button';

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    data: DrawingSummary;
    filters?: MTableFilters | undefined;
}

class DrawingSummaryPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_drawingClient: NSwagAdapter<DrawingClient>;
    private readonly m_lockClient: NSwagAdapter<LockClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private readonly m_dataTableRef: RefObject<DataTable>;

    private month: number;
    private year: number;

    private pounds = Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    });

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_drawingClient = new NSwagAdapter<DrawingClient>(new DrawingClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_lockClient = new NSwagAdapter<LockClient>(new LockClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_messageDialogRef = createRef<MessageDialog>();
        this.m_dataTableRef = createRef<DataTable>();

        const currentDate = new Date();
        this.month = currentDate.getMonth();
        this.year = currentDate.getFullYear();

        this.initializeState();

    }

    private bindMethods(): void {
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.onMonthChange = this.onMonthChange.bind(this);
        this.onYearChange = this.onYearChange.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.drawnByTemplate = this.drawnByTemplate.bind(this);
        this.paidTemplate = this.paidTemplate.bind(this);
        this.paidTotalTemplate = this.paidTotalTemplate.bind(this);
        this.chargeTemplate = this.chargeTemplate.bind(this);
        this.chargeTotalTemplate = this.chargeTotalTemplate.bind(this);
        this.netProfitTemplate = this.netProfitTemplate.bind(this);
        this.getDrawnByFilterTemplate = this.getDrawnByFilterTemplate.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.exportDataClick = this.exportDataClick.bind(this);
        this.exportFunction = this.exportFunction.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        const filters: MTableFilters = {
            'drawing.drawingNum': {
                value: null,
                matchMode: FilterMatchMode.CONTAINS
            },
            'request.estimate': {
                value: null,
                matchMode: FilterMatchMode.CONTAINS
            },
            'request.exchange': {
                value: null,
                matchMode: FilterMatchMode.CONTAINS
            },
            'request.postcode': {
                value: null,
                matchMode: FilterMatchMode.CONTAINS
            },
            'drawing.drawnBy': {
                value: null,
                matchMode: FilterMatchMode.IN
            },
            'drawing.numOfDrawings': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.requestDate': {
                value: null,
                matchMode: FilterMatchMode.DATE_IS
            },
            'drawing.createdOn': {
                value: null,
                matchMode: FilterMatchMode.DATE_IS
            },
            'drawing.turnAround': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.paidPerPlan': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.totalPaid': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.chargePerPlan': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.totalCharged': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            },
            'drawing.netProfit': {
                value: null,
                matchMode: FilterMatchMode.EQUALS
            }
        };

        this.state = {
            page: page,
            data: new DrawingSummary(),
            filters: filters
        };

    }

    private onMonthChange(newValue: MNullable<number>) : void {
        this.month = newValue!;
        this.page_onLoadData();
    }

    private onYearChange(newValue: InputNumberChangeParams) : void {
        this.year = newValue.value!;
        this.page_onLoadData();
    }

    private async table_onSelectionChange(selection: DrawingRegister | DrawingRegister[] | undefined): Promise<void> {

        const navigateTo = selection as DrawingRegister | undefined
        if(navigateTo !== undefined) {
            const userName = this.props.msalContext.accounts[0].username;

            const lockResult = await this.m_lockClient.callWithReply(
                LockReply, (client) => client.get(navigateTo!.request.id)
            );
            lockResult.throwIfFailed();
            if(lockResult.reply!.lockData!.isLocked && lockResult.reply!.lockData!.lockingUser !== userName){
                this.m_messageDialogRef.current!.show(
                    'Booking in use',
                    `Booking ${navigateTo.request.estimate} is currently in use by '${lockResult.reply!.lockData!.lockingUser}'`,
                    MSuccessIcon);
            }
            else{
                await this.m_lockClient.call(
                    (client) => client.create(new CreateLockRequest(
                        {
                            itemId: navigateTo.request.id
                        })
                    ));
                this.props.navigate(`/bookings/${navigateTo!.request.id}`);
            }
        }

    }

    private async page_onLoadData(): Promise<void> {
        //Get Summary Data
        const summaryResult = await this.m_drawingClient.callWithReply(
            DrawingSummaryReply, (client) => client.getMonthReport(this.month + 1, this.year));
        summaryResult.throwIfFailed();
        summaryResult.reply!.summary!.drawingList.forEach(d => {
            const timeDiff = d.drawing.createdOn.getTime() - (d.drawing.requestDate?.getTime() ?? new Date().getTime());
            const diffDays = Math.round(timeDiff / (1000 * 3600 * 24));

            d.drawing.turnAround = diffDays;
            d.drawing.totalPaid = d.drawing.numOfDrawings * d.drawing.paidPerPlan;
            d.drawing.totalCharged = d.drawing.numOfDrawings * d.drawing.chargePerPlan;
            d.drawing.netProfit = (d.drawing.numOfDrawings * d.drawing.chargePerPlan) -
                (d.drawing.numOfDrawings * d.drawing.paidPerPlan);
        })

        this.safeSetState((draft: State) => {

            draft.data = summaryResult.reply!.summary!;

        });
    }

    private generateMonthEntries(): MDropDownItem<number>[] {
        const date = new Date();
        const monthEntries: MDropDownItem<number>[] = [];

        for(let m: number = 0; m < 12; m++) {
            date.setMonth(m);
            monthEntries.push({
                label: date.toLocaleString('en-GB', { month: 'long' }),
                value: m
            });
        }

        return monthEntries;
    }

    private exportFunction(e : DataTableExportFunctionParams){
        if(e.field === "drawing.drawnBy") {
            if(e.rowData.drawing.drawnBy > 0) {
                const index = this.context.enums?.drawnByValues.findIndex(value => value.id === e.rowData.drawing.drawnBy);

                if(index === undefined || index === -1) {
                    return null;
                }

                return this.context.enums?.drawnByValues[index!].name!;
            }

            return e.rowData.drawing.createdBy;

        }
        return e.data
    }

    private exportDataClick() {
        this.m_dataTableRef.current!.exportCSV({selectionOnly: false});
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MBackButton disabled={false}/>
                <MSpacer/>
                <MDropDown<number> id="monthSelect"
                                   disabled={false}
                                   items={this.generateMonthEntries()}
                                   initialValue={this.month}
                                   onChange={this.onMonthChange}
                />
                <MSpacer/>
                <InputNumber showButtons
                             incrementButtonIcon="pi pi-plus"
                             decrementButtonIcon="pi pi-minus"
                             buttonLayout="horizontal"
                             step={1}
                             value={this.year}
                             useGrouping={false}
                             onChange={this.onYearChange}
                />
            </MToolbar>
        );
    }

    private drawnByTemplate(rowData: DrawingRegister): string | null {
        if(rowData.drawing.drawnBy > 0) {
            const index = this.context.enums?.drawnByValues.findIndex(value => value.id === rowData.drawing.drawnBy);

            if(index === undefined || index === -1) {
                return null;
            }

            return this.context.enums?.drawnByValues[index!].name!;
        }

        return rowData.drawing.createdBy;
    }

    private requestDateTemplate(rowData: DrawingRegister): string | null {
        if(rowData.drawing.requestDate !== undefined && rowData.drawing.requestDate !== null){
            return rowData.drawing.requestDate.toLocaleDateString();
        }

        return '';
    }

    private completedTemplate(rowData: DrawingRegister): string | null {
        if(rowData.drawing.createdOn !== undefined && rowData.drawing.createdOn !== null){
            return rowData.drawing.createdOn.toLocaleDateString();
        }

        return '';
    }

    private paidTemplate(rowData: DrawingRegister): string | null {
        return this.pounds.format(rowData.drawing.paidPerPlan);
    }

    private paidTotalTemplate(rowData: DrawingRegister): string | null {
        return this.pounds.format(rowData.drawing.totalPaid!);
    }

    private chargeTemplate(rowData: DrawingRegister): string | null {
        return this.pounds.format(rowData.drawing.chargePerPlan);
    }

    private chargeTotalTemplate(rowData: DrawingRegister): string | null {
        return this.pounds.format(rowData.drawing.totalCharged!);
    }

    private netProfitTemplate(rowData: DrawingRegister): string | null {
        return this.pounds.format(rowData.drawing.netProfit!);
    }

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private getDrawnByFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList: MDropDownItem<number>[] = (
            this.context.enums!.drawnByValues
                .map((enumValue) => {
                    return {label: enumValue.name, value: enumValue.id};
                })
        );

        return (
            <MMultiDropDown id="drawnByFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    public render(): ReactElement {
        return (
            <MPage title="Drawings"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MessageDialog ref={this.m_messageDialogRef}/>

                <Divider/>

                <MFormSection title="Summary">
                    <div className="col-12 md:col-6">
                        <TurnaroundControl TurnaroundTime={this.state.data.averageTurnaround}
                                           SummaryList={this.state.data.drawingMonthlySummaries}
                                           Totals={this.state.data.drawingMonthlyTotals}/>
                    </div>

                    <div className="col-12 md:col-2">
                        <MonthTotalsControl MonthTotals={this.state.data.monthTotals} Year={this.year}/>
                    </div>
                </MFormSection>

                <Divider/>

                <MFormSection title="Drawing Register">
                    <div className="col-12 md:col-12">
                        <div>
                            <MButton
                                disabled={false}
                                label={"Export Data"}
                                onClick={this.exportDataClick}
                            />
                        </div>
                        <MTable
                            innerRef={this.m_dataTableRef}
                            rowKey="label"
                            rowData={this.state.data.drawingList}
                            sortMode={'multiple'}
                            onSelectionChange={this.table_onSelectionChange}
                            showSelection={false}
                            multiSortMeta={[{field: 'drawing.drawingNum', order: 1}]}
                            exportFunction={this.exportFunction}
                            exportName={'DrawingExport'}>

                            <Column field="drawing.drawingNum"
                                    header="Drawing Number"
                                    filter={true}
                                    sortable={true}
                                    style={{width: '15%'}}/>
                            <Column field="request.estimate"
                                    header="Estimate"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '15%'}}/>
                            <Column field="request.exchange"
                                    header="Exchange"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '15%'}}/>
                            <Column field="request.postcode"
                                    header="Post Code"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '15%'}}/>
                            <Column field="drawing.drawnBy"
                                    header="Drawn By"
                                    body={this.drawnByTemplate}
                                    filter={true}
                                    filterElement={this.getDrawnByFilterTemplate}
                                    filterMatchMode={'in'}
                                    showFilterMatchModes={false}
                                    showFilterOperator={false}
                                    maxConstraints={1}
                                    sortable={false}
                                    style={{width: '15%'}}/>
                            <Column field="drawing.numOfDrawings"
                                    header="Drawing Count"
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.requestDate"
                                    header="Request Date"
                                    body={this.requestDateTemplate}
                                    dataType="date"
                                    filter={true}
                                    filterElement={this.dateFilterTemplate}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.createdOn"
                                    header="Date Completed"
                                    body={this.completedTemplate}
                                    dataType="date"
                                    filter={true}
                                    filterElement={this.dateFilterTemplate}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.turnAround"
                                    header="Turn Around"
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.paidPerPlan"
                                    header="Paid Per Plan"
                                    body={this.paidTemplate}
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.totalPaid"
                                    header="Total Paid"
                                    body={this.paidTotalTemplate}
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.chargePerPlan"
                                    header="Charge Per Plan"
                                    body={this.chargeTemplate}
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.totalCharged"
                                    header="Total Charged"
                                    body={this.chargeTotalTemplate}
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                            <Column field="drawing.netProfit"
                                    header="Net Profit"
                                    body={this.netProfitTemplate}
                                    dataType="numeric"
                                    filter={true}
                                    sortable={false}
                                    style={{width: '10%'}}/>
                        </MTable>
                    </div>
                </MFormSection>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(DrawingSummaryPage));
